// Englische Fassung von ../de/shared.ts.
import type { SharedKatalog } from "../de/shared";

export const shared: SharedKatalog = {
  projekteBaum: {
    leer: "No projects yet. Create one on the Projects page.",
    neuesProjekt: "New project",
    umbenennen: "Rename project",
    umbenennenFehlgeschlagen: "Renaming the project failed",
    nameFeldAria: "Project name",
    zeileTitel: "Click: open · Double-click: rename",
    schliessen: "Close",
  },

  // Wortwahl aus den bestehenden Katalogen übernommen, damit dieselbe Aktion
  // nicht zweimal anders heißt: „Open the command palette" und „Focus the
  // search" wie mail.kuerzel.aktion, „Meeting from this email" und „Insert
  // template" wie mail.entwurf, „Find a time" wie calendar.kopf.terminFinden.
  palette: {
    platzhalter: "Search, action, jump …",
    oeffnen: "Open the command palette",
    knopfTitel: (taste) => `Command palette (${taste})`,
    gruppeAktionen: "Actions",
    keineTreffer: (suche) => `No matches for “${suche}”.`,
    fussNavigieren: "navigate",
    fussOeffnen: "open",
    fussSchliessen: "close",

    aktionen: {
      quickNote: "Quick capture: new note",
      uebersicht: "Overview",
      notizen: "Notes",
      frageStellen: "Ask a question",
      dokumentHochladen: "Upload document",
      projekte: "Projects",
      zeitachse: "Timeline",
      tabellen: "Tables",
      mailSuche: "Mail: focus the search",
      mailTermin: "Mail: meeting from this email",
      mailKi: "Mail: suggest an AI reply",
      mailVorlage: "Mail: insert template",
      mailSf: "Mail: ask Salesforce",
      mailKuerzel: "Mail: keyboard shortcuts",
      kalender: "Open the calendar",
      kalenderTermin: "Calendar: find a time",
    },

    trefferTyp: {
      note: "Note",
      task: "Task",
      document: "Document",
      sf_project: "SF project",
      sf_action: "SF action",
      chat_message: "Chat",
    },
  },

  rahmen: {
    profilMenue: "Profile menu",
    sidebarSchliessen: "Close sidebar",
    sidebarOeffnen: "Open sidebar",

    statistik: "Statistics",
    einstellungen: "Settings",
    abmelden: "Sign out",

    designHell: "Light",
    designDunkel: "Dark",
    designHellAria: "Light theme",
    designDunkelAria: "Dark theme",
  },

  projektReiter: {
    chats: "Chats",
    aufgaben: "Tasks",
    artefakte: "Artefacts",
    intake: "Intake",
  },

  flotte: {
    titel: "Fleet",
    aktiv: "active",
    schalterAria: "Turn the fleet on or off for this project",
    titelAn:
      "Fleet on — the loop fleet may start loops for this project. Click to switch it off.",
    titelAus: "Fleet off — click to let the loop fleet work on this project.",
  },

  instruktionen: {
    titel: "Instructions",
    platzhalter: "e.g. 'Answer formally. Give internal studies priority.'",
    laedt: "Loading …",
    feldAria: "Instructions for this project",
    feldTitel: "Put in front of every system prompt.",
    gespeichert: "Instructions saved",
  },

  wissen: {
    titel: "Knowledge",
    dateiKnopf: "File",
    dateiHinzufuegen: "Add file",
    dateiFeldAria: "Upload knowledge file (PDF/Markdown)",
    ablage: "Drop a PDF/MD here or click plus",
    summaryLaeuft: "Summary is being generated …",
    loeschenFrage: (titel: string) => `Delete “${titel}”?`,
    hochgeladen: "Uploaded",
    uploadFehlgeschlagen: "Upload failed",
    loeschenFehlgeschlagen: "Deleting failed",
    externeQuellen: "External sources",
  },

  composer: {
    platzhalter: "What can Atlas help with?",
    feldAria: "Ask a question or capture a note",
    optionen: "Options — project, research, attachment",
    optionenSchliessen: "Close options",
    zielProjekt: "Target project",
    allgemein: "General",
    dokumentAnhaengen: "Attach document",
    dateiTypen: "PDF · MD · Text",
    alsNotizSpeichern: "Save as note",
    senden: "Send",
    frageFehlgeschlagen: "Question failed",
    notizGespeichert: "Note saved",
  },

  personen: {
    platzhalter: "Search employees …",
    entfernen: (name: string) => `Remove ${name}`,
    laedt: "loading …",
    keineTreffer: "no matches",
    keineTrefferFuer: (suche: string) => `no matches for “${suche}”`,
  },

  intakeStatus: {
    raw: "Raw",
    questions: "Open questions",
    answered: "Answered",
    planning: "Planning",
    planned: "Planned",
  },

  termine: {
    standUm: (zeit: string) => `As of ${zeit}`,
    standUnbekannt: "As of —",
    nichtErreichbar: " · unreachable",
    aktualisieren: "Refresh appointments",
    keineTermine: (tage: number) => `No appointments in the next ${tage} days`,
    listeAria: (tage: number) => `Appointments for the next ${tage} days`,
    naechsterTermin: (betreff: string, wann: string) => `Next appointment: ${betreff} — ${wann}`,
    beitreten: "Join",
    laeuft: "running",
    inMin: (n: number) => `in ${n} min`,
    umUhr: (zeit: string) => `at ${zeit}`,
    heute: "Today",
    morgen: "Tomorrow",
  },

  useCases: {
    ask: {
      titel: "Ask your knowledge",
      beschreibung: "Put questions to your documents and project knowledge.",
    },
    upload: {
      titel: "Upload a document & question it",
      beschreibung: "Attach a PDF and question its content right in the chat.",
      chip: "Upload document",
    },
    note: {
      titel: "Capture a note",
      beschreibung: "Hold on to a thought in seconds.",
      chip: "New note",
    },
    tasks: {
      titel: "My tasks",
      beschreibung: "See what is due — kanban, calendar, timeline.",
      chip: "My tasks",
    },
    appointment: {
      titel: "Find an appointment",
      beschreibung: "Find free time slots for a meeting.",
      chip: "Appointment",
    },
    email: {
      titel: "Mail & replies",
      beschreibung: "Summarise mailbox threads and draft replies.",
    },
    blog: {
      titel: "Write a blog article",
      beschreibung: "Produce well-founded specialist articles from your knowledge.",
      chip: "Blog article",
    },
    video: {
      titel: "Create a video",
      beschreibung: "Produce avatar videos from a script.",
      chip: "Video",
    },
    claims: {
      titel: "Check claims",
      beschreibung: "Check advertising claims for compliance and evidence.",
    },
    datasets: {
      titel: "Tables from documents",
      beschreibung: "Extract structured tables from documents.",
    },
  },
};
